import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useStore } from 'vuex'
import { computed } from 'vue'

// Extend dayjs with relative time plugin
dayjs.extend(relativeTime)

/**
 * useDateFormatter composable
 * Returns date formatting functions that apply user-configured formats.
 * All formatters are reactive and update when locale settings change.
 */
export function useDateFormatter() {
  const store = useStore()
  
  // Reactive locale and date format from store
  const locale = computed(() => store.state.settings?.general?.locale || 'en-US')
  const dateFormat = computed(() => store.state.settings?.general?.dateFormat || 'YYYY-MM-DD')
  
  const formatDate = (date) => {
    if (!date) return ''
    return dayjs(date).format(dateFormat.value)
  }
  
  const formatDateTime = (date, options = {}) => {
    if (!date) return ''
    const includeSeconds = options.includeSeconds !== false // default true
    const timeFormat = includeSeconds ? 'HH:mm:ss' : 'HH:mm'
    return dayjs(date).format(`${dateFormat.value} ${timeFormat}`)
  }
  
  const formatTimeAgo = (date) => {
    if (!date) return ''
    return dayjs(date).fromNow()
  }
  
  const formatTime = (date, options = {}) => {
    if (!date) return ''
    const includeSeconds = options.includeSeconds !== false // default true
    return dayjs(date).format(includeSeconds ? 'HH:mm:ss' : 'HH:mm')
  }
  
  const formatDateOnly = (date) => {
    if (!date) return ''
    return dayjs(date).format(dateFormat.value)
  }
  
  // Additional formatting options
  const formatShortDate = (date) => {
    if (!date) return ''
    // Use locale-specific short date format
    return new Intl.DateTimeFormat(locale.value, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(new Date(date))
  }
  
  const formatLongDate = (date) => {
    if (!date) return ''
    // Use locale-specific long date format
    return new Intl.DateTimeFormat(locale.value, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    }).format(new Date(date))
  }
  
  const formatDateTimeWithLocale = (date, options = {}) => {
    if (!date) return ''
    const includeSeconds = options.includeSeconds !== false
    return new Intl.DateTimeFormat(locale.value, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: includeSeconds ? '2-digit' : undefined,
      hour12: false
    }).format(new Date(date))
  }
  
  const formatTimeWithLocale = (date, options = {}) => {
    if (!date) return ''
    const includeSeconds = options.includeSeconds !== false
    return new Intl.DateTimeFormat(locale.value, {
      hour: '2-digit',
      minute: '2-digit',
      second: includeSeconds ? '2-digit' : undefined,
      hour12: false
    }).format(new Date(date))
  }
  
  return { 
    formatDate, 
    formatDateTime, 
    formatTimeAgo, 
    formatTime, 
    formatDateOnly,
    formatShortDate,
    formatLongDate,
    formatDateTimeWithLocale,
    formatTimeWithLocale,
    locale,
    dateFormat
  }
} 